import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { fetchOrganizationIfNeeded } from '@pec/aion-ui-core/actions/organization';
import { fetchMainSubscription } from '@pec/aion-ui-core/slices/mainSubscription';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { Box } from '@material-ui/core';
import { localizeSubscriptionExpiration } from '@pec/aion-ui-components/helpers/subscription';
import { useFeature } from 'flagged';

import { UpgradeTile } from '../../containers/Upgrade/UpgradeTile';
import { useTypedSelector, useAppDispatch } from '../../combineReducers';
import { hasRealExpiration, LOGOS } from '../../helpers/newDashboard';
import { greeting } from '../../helpers/greeting';
import { SubscriptionTiers } from '@pec/aion-ui-core/enums/Subscription';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      display: 'flex',
      flexDirection: 'column',
      gap: `${theme.spacing(2)}px`,
      margin: theme.spacing(2)
    },
    subscriptionContainer: {
      display: 'flex',
      alignItems: 'center',
      flexDirection: 'row',
      gap: `${theme.spacing(1)}px`
    },
    logo: {
      height: 65,
      width: 65
    },
    greeting: {
      fontSize: 24
    },
    subscriptionData: {
      display: 'flex',
      flexDirection: 'row',
      gap: `${theme.spacing(0.5)}px`,
      fontSize: 12
    },
    upgradeButtonContainer: {
      display: 'flex',
      flexGrow: 1,
      justifyContent: 'end'
    }
  })
);

type Props = {
  organizationId: string;
  showMembershipPrefix?: boolean;
};

export const DashboardSubscriptionDetails: React.FC<Props> = ({ organizationId, showMembershipPrefix = true }) => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const { t } = useTranslation('subscriber');
  const { mainSubscription } = useTypedSelector(state => state.mainSubscription);
  const { userInfo } = useTypedSelector(state => state.userInfo);
  const { organization } = useTypedSelector(state => state.organization);
  const mainSubscriptionId = mainSubscription?.id;
  const membershipNumber =
    organization?.organizationRegistrationNumbers?.find(regNumber => regNumber.typeId === 5)?.value || null;

  const recurringBillingMessageFeatureEnabled = useFeature('RecurringBillingAutoRenewalStatusMessageEnabled');

  useEffect(() => {
    if (organizationId) {
      dispatch(fetchOrganizationIfNeeded(organizationId));
    }
  }, [organizationId, dispatch]);

  useEffect(() => {
    if (organizationId && !mainSubscriptionId) {
      dispatch(fetchMainSubscription(organizationId));
    }
  }, [organizationId, mainSubscriptionId, dispatch]);

  if (!mainSubscription) {
    return null;
  }

  const {
    countryISO2,
    description,
    expirationDate,
    typePublicId,
    previousExpirationDate,
    recurringBillingEnabled
  } = mainSubscription;
  const Logo = countryISO2 === 'US' ? LOGOS['US'] : LOGOS[typePublicId];
  const isExpired = expirationDate ? moment(expirationDate).isBefore() : false;
  const isFoundation = description === SubscriptionTiers.Foundation;
  const isRecurringBillingMessageEnabled = recurringBillingMessageFeatureEnabled && recurringBillingEnabled;
  const expiredLabel = showMembershipPrefix
    ? t('newDashboard.subscriptionDetails.expired', 'Membership Expired')
    : t('newDashboard.subscriptionDetails.expiredPlain', 'Expired');
  const expiresLabel = showMembershipPrefix
    ? t('newDashboard.subscriptionDetails.expires', 'Membership Expires')
    : t('newDashboard.subscriptionDetails.expiresPlain', 'Expires');

  return (
    <Box className={classes.container}>
      <Box className={classes.subscriptionContainer}>
        {Logo ? <Logo className={classes.logo} /> : null}
        <Box>
          <Box className={classes.greeting}>
            {greeting(t)}
            {userInfo?.firstName ? `, ${userInfo.firstName}` : ''}
          </Box>
          <Box className={classes.subscriptionData}>
            <Box>{description}</Box>
            {membershipNumber ? (
              <>
                <Box>|</Box>
                <Box>{membershipNumber}</Box>
              </>
            ) : null}
            {hasRealExpiration(expirationDate) && (
              <>
                <Box>|</Box>
                <Box>
                  {isRecurringBillingMessageEnabled
                    ? t('newDashboard.subscriptionDetails.autoRenew', 'Membership is set to auto-renew')
                    : isExpired
                    ? expiredLabel
                    : expiresLabel}{' '}
                  {localizeSubscriptionExpiration(expirationDate, t)}
                </Box>
              </>
            )}
            {isFoundation && previousExpirationDate && (
              <>
                <Box>|</Box>
                <Box>
                  {expiredLabel} {localizeSubscriptionExpiration(previousExpirationDate, t)}
                </Box>
              </>
            )}
          </Box>
        </Box>
        <Box className={classes.upgradeButtonContainer}>
          <UpgradeTile />
        </Box>
      </Box>
    </Box>
  );
};
